import React from 'react';
import propTypes from 'prop-types';
import { FormGroup, Label, Input, Row, Col, Badge } from 'reactstrap';
import ChecklistBox, { singleOf, idsOf } from '../../../components/Export/ChecklistBox';
import { loadBrands, loadRarities, loadSubcategories, loadVariantGroups } from '../../../utils/exportOptions';

const sectionStyle = { fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' };
const labelStyle = { fontWeight: 600, fontSize: '.78rem' };

export const ACTIVE_OPTIONS = [{ id: '1', name: 'Active' }, { id: '0', name: 'Inactive' }];
export const HOT_OPTIONS = [{ id: '1', name: "Editors' choice" }, { id: '0', name: "Not editors' choice" }];
export const AUTO_SYNC_OPTIONS = [{ id: '1', name: 'Auto scrape on' }, { id: '0', name: 'Auto scrape off' }];
export const LISTINGS_OPTIONS = [{ id: 'with', name: 'With an active listing' }, { id: 'without', name: 'Without any listing' }];

export const VARIANT_EXPORT_DEFAULTS = {
  brands: [],
  keyword: '',
  active: [],
  editors_choice: [],
  auto_sync: [],
  rarities: [],
  variant_groups: [],
  subcategories: [],
  languages: [],
  listings: [],
  lowest_ask_min: '',
  lowest_ask_max: '',
  date_field: 'release_date',
  date_from: '',
  date_to: ''
};

const Field = ({ id, label, children }) => (
  <FormGroup>
    <Label for={id} className="mb-1" style={labelStyle}>{label}</Label>
    {children}
  </FormGroup>
);

// VariantExportFilters is the Product Variants block inside ExportModal.
// Every option control is a ChecklistBox; loadLanguages reads
// GET /admins/productvariants/languages so the list shows the values the
// catalogue holds.
export default function VariantExportFilters({ filters, setFilter, disabled, loadLanguages }) {
  const box = (name, props) => (
    <ChecklistBox id={`_exportVariant_${name}`} value={filters[name]} onChange={v => setFilter(name, v)} disabled={disabled} {...props} />
  );
  return (
    <div>
      <div className="text-muted mb-2" style={sectionStyle}>Catalogue</div>
      <Row>
        <Col md={6}><Field id="_exportVariant_brands" label="Brand">{box('brands', { loadOptions: loadBrands, searchable: true, placeholder: 'All brands' })}</Field></Col>
        <Col md={6}><Field id="_exportVariant_keyword" label="Keyword">
          <Input id="_exportVariant_keyword" bsSize="sm" value={filters.keyword} disabled={disabled} placeholder="Display name, SKU, colour" onChange={e => setFilter('keyword', e.target.value)} />
        </Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportVariant_active" label="Active">{box('active', { options: ACTIVE_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVariant_editors_choice" label="Hot product">{box('editors_choice', { options: HOT_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVariant_auto_sync" label="Auto scrape">{box('auto_sync', { options: AUTO_SYNC_OPTIONS })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Card</div>
      <Row>
        <Col md={6}><Field id="_exportVariant_rarities" label="Rarity">{box('rarities', { loadOptions: loadRarities, searchable: true, placeholder: 'All rarities' })}</Field></Col>
        <Col md={6}><Field id="_exportVariant_variant_groups" label="Variant group / set">{box('variant_groups', { loadOptions: loadVariantGroups, searchable: true, placeholder: 'All sets' })}</Field></Col>
      </Row>
      <Row>
        <Col md={6}><Field id="_exportVariant_languages" label="Language">{box('languages', { loadOptions: loadLanguages, placeholder: 'All languages' })}</Field></Col>
        <Col md={6}><Field id="_exportVariant_subcategories" label="Subcategory">{box('subcategories', { loadOptions: loadSubcategories, searchable: true, placeholder: 'All subcategories' })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Stock <Badge color="light" className="border" style={{ fontSize: '.6rem' }}>email only</Badge></div>
      <Row>
        <Col md={4}><Field id="_exportVariant_listings" label="Listings">{box('listings', { options: LISTINGS_OPTIONS, placeholder: 'All variants' })}</Field></Col>
        <Col md={4}><Field id="_exportVariant_lowest_ask_min" label="Lowest ask from (Rp)">
          <Input id="_exportVariant_lowest_ask_min" bsSize="sm" type="number" min="0" value={filters.lowest_ask_min} disabled={disabled} onChange={e => setFilter('lowest_ask_min', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportVariant_lowest_ask_max" label="Lowest ask to (Rp)">
          <Input id="_exportVariant_lowest_ask_max" bsSize="sm" type="number" min="0" value={filters.lowest_ask_max} disabled={disabled} onChange={e => setFilter('lowest_ask_max', e.target.value)} />
        </Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Dates</div>
      <Row>
        <Col md={4}><Field id="_exportVariant_date_field" label="Date field">
          <Input id="_exportVariant_date_field" bsSize="sm" type="select" value={filters.date_field} disabled={disabled} onChange={e => setFilter('date_field', e.target.value)}>
            <option value="release_date">Release date</option>
            <option value="created_at">Created at</option>
            <option value="updated_at">Updated at</option>
          </Input>
        </Field></Col>
        <Col md={4}><Field id="_exportVariant_date_from" label="From">
          <Input id="_exportVariant_date_from" bsSize="sm" type="date" value={filters.date_from} disabled={disabled} onChange={e => setFilter('date_from', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportVariant_date_to" label="To">
          <Input id="_exportVariant_date_to" bsSize="sm" type="date" value={filters.date_to} disabled={disabled} onChange={e => setFilter('date_to', e.target.value)} />
        </Field></Col>
      </Row>
    </div>
  );
}

VariantExportFilters.propTypes = {
  filters: propTypes.object.isRequired,
  setFilter: propTypes.func.isRequired,
  disabled: propTypes.bool,
  loadLanguages: propTypes.func.isRequired
};


// toVariantParams maps the modal's values onto Go /admins/productvariants
// query parameters. The panel's own params come first; an empty box inherits
// the panel's value, a pick overrides it. Two-option boxes narrow only when
// exactly one option is ticked.
export const toVariantParams = (panelParams, f) => {
  const { page, per_page, sort_by, ...base } = panelParams;
  const params = { ...base };
  const override = (key, value) => {
    if (value !== '' && value !== null && value !== undefined) params[key] = value;
  };
  override('keyword', (f.keyword || '').trim());
  override('active', singleOf(f.active, ACTIVE_OPTIONS));
  override('editors_choice', singleOf(f.editors_choice, HOT_OPTIONS));
  override('auto_sync', singleOf(f.auto_sync, AUTO_SYNC_OPTIONS));
  override('brand_id', idsOf(f.brands));
  override('rarity_id', idsOf(f.rarities));
  override('variant_group_id', idsOf(f.variant_groups));
  override('subcategory_id', idsOf(f.subcategories));
  override('language', idsOf(f.languages));
  if (f.date_from || f.date_to) {
    override('date_field', f.date_field);
    override('date_from', f.date_from);
    override('date_to', f.date_to);
  }
  return params;
};

export const toVariantStockParams = f => {
  const params = {};
  const listings = singleOf(f.listings, LISTINGS_OPTIONS);
  if (listings) params.listings = listings;
  if (f.lowest_ask_min !== '') params.lowest_ask_min = String(f.lowest_ask_min);
  if (f.lowest_ask_max !== '') params.lowest_ask_max = String(f.lowest_ask_max);
  return params;
};

export const hasStockFilters = f => Boolean(singleOf(f.listings, LISTINGS_OPTIONS) || f.lowest_ask_min !== '' || f.lowest_ask_max !== '');
